import type { CSSProperties, FC, ReactNode } from 'react'
import type { IntegrationCategory } from '@/constants/integrations'

import Link from 'next/link'
import { useState } from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LogoMark } from '@/components/generals/logo'
import { Eyebrow } from '@/components/generals/eyebrow'
import { Container } from '@/components/generals/container'
import { RevealText } from '@/components/generals/reveal-text'

import {
    CATEGORY_LABELS,
    CATEGORY_STYLES,
    INTEGRATIONS,
    INTEGRATION_CATEGORIES
} from '@/constants/integrations'

const OUTER_RING = ['QuickBooks Online', 'Stripe', 'Slack', 'NetSuite', 'Gusto', 'Plaid']
const INNER_RING = ['Xero', 'Salesforce', 'Google Sheets', 'Brex']

const findIntegration = (name: string) => INTEGRATIONS.find(item => item.name === name)

const Orbit: FC = (): ReactNode => {
    const rings = [
        { names: OUTER_RING, radius: 47, spin: 'animate-orbit-slow', counter: 'animate-orbit-slow-reverse', offset: -90 },
        { names: INNER_RING, radius: 30, spin: 'animate-orbit-reverse', counter: 'animate-orbit', offset: -45 }
    ]

    return (
        <div aria-hidden='true' className='relative mx-auto aspect-square w-full max-w-[520px]'>
            <div className='absolute inset-0 rounded-full border border-burgundy/10' />
            <div className='absolute inset-[17%] rounded-full border border-dashed border-burgundy/15' />
            <div className='absolute inset-[34%] rounded-full border border-burgundy/10' />

            <div className='absolute inset-[39%] flex items-center justify-center rounded-full bg-burgundy shadow-[0_0_0_14px_rgb(57_26_24/0.05),0_30px_60px_-20px_rgb(57_26_24/0.5)]'>
                <LogoMark className='size-1/2 text-gold' />
            </div>

            {rings.map(ring => (
                <div key={ring.radius} className={cn('absolute inset-0', ring.spin)}>
                    {ring.names.map((name, index) => {
                        const integration = findIntegration(name)
                        const angle = ((360 / ring.names.length) * index + ring.offset) * (Math.PI / 180)
                        const left = 50 + ring.radius * Math.cos(angle)
                        const top = 50 + ring.radius * Math.sin(angle)

                        if (!integration) return null

                        return (
                            <div
                                key={name}
                                className='absolute -translate-x-1/2 -translate-y-1/2'
                                style={{ left: `${left.toFixed(3)}%`, top: `${top.toFixed(3)}%` }}
                            >
                                <div className={ring.counter}>
                                    <span className='flex items-center gap-2 whitespace-nowrap rounded-full border border-burgundy/10 bg-ivory py-1 pl-1 pr-3 text-[11px] font-medium text-burgundy shadow-[0_10px_24px_-14px_rgb(57_26_24/0.45)]'>
                                        <span className={cn('flex size-6 items-center justify-center rounded-full text-[9px] font-semibold', CATEGORY_STYLES[integration.category])}>
                                            {integration.short}
                                        </span>
                                        {integration.name}
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            ))}
        </div>
    )
}

interface IntegrationsProps {
    tone?: 'paper' | 'cream'
}

export const Integrations: FC<IntegrationsProps> = ({ tone = 'paper' }): ReactNode => {
    const [category, setCategory] = useState<IntegrationCategory | 'all'>('all')
    const visible = category === 'all' ? INTEGRATIONS : INTEGRATIONS.filter(item => item.category === category)

    return (
        <section id='integrations' aria-labelledby='integrations-title' className={tone === 'paper' ? 'bg-paper' : 'bg-cream'}>
            <Container className='py-28 md:py-40'>
                <div className='grid gap-16 lg:grid-cols-12 lg:items-center lg:gap-10'>
                    <div className='lg:col-span-5'>
                        <Eyebrow>Integrations</Eyebrow>

                        <RevealText
                            as='h2'
                            id='integrations-title'
                            text='Works with the tools you already trust.'
                            highlight={['already trust.']}
                            highlightClassName='text-stone'
                            className='mt-6 font-heading text-display-lg font-medium text-burgundy'
                        />

                        <p data-reveal='' className='mt-6 max-w-md text-lg leading-relaxed text-stone-dark'>
                            Connect accounting, banking, billing, payroll and the apps your team lives in. Data flows in continuously, so every dashboard reflects what happened minutes ago, not last month.
                        </p>

                        <div data-reveal='' role='group' aria-label='Filter integrations by category' className='mt-8 flex flex-wrap gap-2'>
                            {INTEGRATION_CATEGORIES.map(item => (
                                <button
                                    key={item.id}
                                    type='button'
                                    aria-pressed={category === item.id}
                                    onClick={() => setCategory(item.id)}
                                    className={cn(
                                        'rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors duration-300',
                                        category === item.id
                                            ? 'border-burgundy bg-burgundy text-cream'
                                            : 'border-burgundy/15 text-stone-dark hover:border-burgundy/35 hover:text-burgundy'
                                    )}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div data-reveal='scale' className='lg:col-span-6 lg:col-start-7'>
                        <Orbit />
                    </div>
                </div>

                <ul
                    aria-live='polite'
                    className='mt-20 grid grid-cols-2 border-l border-t border-burgundy/10 sm:grid-cols-3 lg:grid-cols-6'
                >
                    {visible.map((integration, index) => (
                        <li
                            key={integration.name}
                            className='group relative animate-in border-b border-r border-burgundy/10 p-5 transition-colors duration-500 fade-in hover:bg-ivory'
                            style={{ animationDelay: `${index * 25}ms` } as CSSProperties}
                        >
                            <span
                                aria-hidden='true'
                                className={cn(
                                    'flex size-10 items-center justify-center rounded-xl text-xs font-semibold transition-transform duration-500 ease-out-expo group-hover:-translate-y-0.5 group-hover:scale-105',
                                    CATEGORY_STYLES[integration.category]
                                )}
                            >
                                {integration.short}
                            </span>

                            <p className='mt-5 text-sm font-medium text-burgundy'>{integration.name}</p>
                            <p className='mt-1 text-xs text-stone'>{CATEGORY_LABELS[integration.category]}</p>

                            <p className='mt-3 text-[11px] font-medium text-gold-dark opacity-0 transition-opacity duration-300 group-hover:opacity-100 max-lg:opacity-100'>
                                {integration.sync}
                            </p>
                        </li>
                    ))}
                </ul>

                <p className='mt-8 flex flex-wrap items-center gap-2 text-sm text-stone-dark'>
                    Need something else? Enterprise plans include custom integrations and API access.
                    <Link href='/contact' className='group inline-flex items-center gap-1 font-medium text-burgundy underline-offset-4 hover:underline'>
                        Request an integration
                        <ArrowRight aria-hidden='true' className='size-3.5 transition-transform duration-300 group-hover:translate-x-0.5' />
                    </Link>
                </p>
            </Container>
        </section>
    )
}
